<script setup>
import { ref } from "vue";
import { Head, Link, useForm } from "@inertiajs/vue3";
import Input from "@pages/Shared/form/Input.vue";
import Button from "@pages/Shared/form/Button.vue";
import Spinner from "@pages/Shared/ui/Spinner.vue";
import { PhEye, PhEyeClosed } from "@phosphor-icons/vue";
import logo from '@/../images/logo.jpg';
import { flashToast } from "@/toast.js";

const form = useForm({
  first_name:            '',
  last_name:             '',
  email:                 '',
  phone:                 '',
  password:              '',
  password_confirmation: '',
});

const showPassword        = ref(false);
const showConfirmPassword = ref(false);

const register = () => {
  form.post('/register', {
    onSuccess: (page) => {
      if (page.props.flash) flashToast(page.props.flash);
    },
    onError: () => {
      flashToast({ status: false, message: 'Please correct the errors below.' });
    },
  });
};
</script>

<template>
  <Head title="Register" />

  <div class="min-h-screen w-full flex items-center justify-center bg-background px-4 py-10">
    <div class="w-full max-w-[500px]">
      <div class="bg-white rounded-lg border border-gray-300 px-8 py-10">

        <!-- Logo -->
        <div class="flex justify-center mb-6">
          <img :src="logo" alt="Logo" class="w-20 h-20 object-contain" />
        </div>

        <!-- Heading -->
        <div class="text-center mb-7">
          <h1 class="text-2xl font-bold text-[#0f1929] tracking-tight">Register to Get Started</h1>
          <p class="mt-1.5 text-sm text-gray-500">Create your applicant account</p>
        </div>

        <!-- Form -->
        <form @submit.prevent="register" class="flex flex-col gap-5">

          <!-- Name row -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input
              v-model="form.first_name"
              :error="form.errors.first_name"
              label="First Name"
              placeholder="First name"
              autofocus
            />
            <Input
              v-model="form.last_name"
              :error="form.errors.last_name"
              label="Last Name"
              placeholder="Last name"
            />
          </div>

          <!-- Email -->
          <Input
            v-model="form.email"
            :error="form.errors.email"
            label="Email"
            type="email"
            placeholder="your@email.com"
          />

          <!-- Phone -->
          <Input
            v-model="form.phone"
            :error="form.errors.phone"
            label="Phone Number"
            type="tel"
            placeholder="+234 000 000 0000"
          />

          <!-- Password row -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">

            <!-- Password -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div class="relative">
                <Input
                  v-model="form.password"
                  :error="form.errors.password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="xxxxxxxx"
                  :hide-label="true"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                  class="absolute right-3 top-[0.6rem] text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <component :is="showPassword ? PhEyeClosed : PhEye" :size="20" />
                </button>
              </div>
            </div>

            <!-- Confirm Password -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <div class="relative">
                <Input
                  v-model="form.password_confirmation"
                  :error="form.errors.password_confirmation"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  placeholder="xxxxxxxx"
                  :hide-label="true"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  tabindex="-1"
                  class="absolute right-3 top-[0.6rem] text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <component :is="showConfirmPassword ? PhEyeClosed : PhEye" :size="20" />
                </button>
              </div>
            </div>

          </div>

          <!-- Submit -->
          <Button
            type="submit"
            :disabled="form.processing"
            class="w-full py-2.5 bg-primary hover:bg-primary-light text-white font-semibold
                   rounded-lg transition-colors flex justify-center items-center gap-2
                   cursor-pointer disabled:opacity-70 shadow-sm mt-1"
          >
            <Spinner v-if="form.processing" :size="16" color="white" />
            <span>{{ form.processing ? 'Creating Account…' : 'Register' }}</span>
          </Button>

        </form>

        <p class="mt-5 text-center text-sm text-gray-500">
          Already have an account?
          <Link
            href="/login"
            class="text-primary font-semibold underline underline-offset-2 hover:text-primary-light transition-colors"
          >
            Login
          </Link>
        </p>

        <p class="mt-4 text-center text-xs text-gray-400">
          &copy; {{ new Date().getFullYear() }} Bn Mas'ud College. All rights reserved.
        </p>

      </div>
    </div>
  </div>
</template>